import type { AnyFilterSchema } from "@p0security/shared/integrations/types";
import {
  Access,
  type Parameter,
  type RequestOf,
} from "@p0security/shared/types/access/index";
import { lowerFirst } from "lodash-es";

type ScimAccessesArgs = {
  entitlement: Pick<Parameter, "description" | "label">;
  group: Pick<Parameter, "description" | "label">;
  locator: Pick<Parameter, "description" | "label">;
  name: string;
};

const commonAccess = {
  delegation: {},
  generated: {},
  maturity: "preview",
  render: { locator: "locatorId", policy: "name" } as const,
} as const;

/** Holds the user-input locator value, which may be omitted if only
 * installed on one location.
 */
const commonLocator = (args: Pick<ScimAccessesArgs, "locator">) => ({
  ...args.locator,
  optional: true as const,
  type: "static" as const,
});
/** Holds the resolved locator value */
const commonLocatorId = (args: Pick<ScimAccessesArgs, "locator">) => ({
  ...args.locator,
  type: "generated" as const,
});
/** Holds the system-internval values of the entitlements / groups for which access is requested */
const commonId = <K extends "entitlement" | "group">(
  key: K,
  args: Pick<ScimAccessesArgs, K>
) => ({
  ...args[key],
  label: `${args[key].label} ID`,
  multivalued: true as const,
  type: "static" as const,
});
/** Holds the human-readable labels of the entitlements / groups for which access is requested */
const commonName = <K extends "entitlement" | "group">(
  key: K,
  args: Pick<ScimAccessesArgs, K>
) => ({
  ...args[key],
  label: `${args[key].label} name`,
  multivalued: true as const,
  type: "generated" as const,
});

export const newScimAccesses = (args: ScimAccessesArgs) => ({
  entitlement: Access({
    ...commonAccess,
    label: args.entitlement.label,
    description: `Assume ${lowerFirst(args.entitlement.label)}s in ${args.name}`,
    parameters: {
      locator: commonLocator(args),
      locatorId: commonLocatorId(args),
      id: commonId("entitlement", args),
      name: commonName("entitlement", args),
    },
  }),
  group: Access({
    ...commonAccess,
    label: args.group.label,
    description: `Assume ${lowerFirst(args.group.label)}s in ${args.name}`,
    parameters: {
      locator: commonLocator(args),
      locatorId: commonLocatorId(args),
      id: commonId("group", args),
      name: commonName("group", args),
    },
  }),
});

const UNUSED = { label: "unused" };
export const unusedScimAccesses = newScimAccesses({
  entitlement: UNUSED,
  group: UNUSED,
  locator: UNUSED,
  name: "unused",
});

export const newScimFilters = (args: ScimAccessesArgs) =>
  ({
    group: { keys: ["id", "name"], label: args.group.label },
    entitlement: { keys: ["id", "name"], label: args.entitlement.label },
  }) as const satisfies AnyFilterSchema;

export type ScimAccesses = ReturnType<typeof newScimAccesses>;
export type ScimEntitlementAccess = ScimAccesses["entitlement"];
export type ScimGroupAccess = ScimAccesses["group"];

export type ScimEntitlementRequest = RequestOf<ScimEntitlementAccess>;
export type ScimGroupRequest = RequestOf<ScimGroupAccess>;
